import { useState } from 'react';
import { StatusChip } from '../../components/data';
import { ErrorBanner, Loading } from '../../components/ui';
import { formatDate, formatNumber } from '../../lib/format';
import { useResource } from '../../lib/hooks';
import type {
  Employee,
  OnboardingSummaryRow,
  SalesAccount,
  SalesOpportunity,
} from '../../lib/types';
import { OnboardingWizard } from './OnboardingWizard';
import { OnboardingWorkspace } from './OnboardingWorkspace';
import { progressPct } from './onboardingMeta';

/**
 * Onboarding — the standalone onboarding app, moved in as a tab on Sales.
 *
 * Everything that app did: the intake wizard, the reusable task library, the
 * per-client task board and the attachments. What did NOT come across is its
 * Firebase back end and the client-facing portal in front of it — clients
 * logged in with a password created in Firebase Auth and ticked their own
 * tasks off. This app is Entra-only and internal, so the work is done here and
 * the client is emailed, which is what the per-task email templates were for
 * anyway. See migration 054.
 *
 * What is new is the far end. The source app had no idea what happened to a
 * client once onboarding finished; here it ends in a handover that makes them
 * an Account Management client against the same company row the sales account
 * already points at.
 */

export function OnboardingTab({
  accounts,
  deals,
  employees,
  defaultAccountId,
  onAccountsChanged,
}: {
  accounts: SalesAccount[];
  deals: SalesOpportunity[];
  employees: Employee[];
  /** The account just created on this page, if there is one. The wizard opens
   *  on it and carries its answers across. */
  defaultAccountId?: number | null;
  /** A new company or a handover changes the account list on the other tabs. */
  onAccountsChanged: () => void;
}) {
  const list = useResource<OnboardingSummaryRow[]>('/sales/onboarding');

  const [wizard, setWizard] = useState(false);
  const [open, setOpen] = useState<number | null>(null);

  const reload = () => {
    list.reload();
    onAccountsChanged();
  };

  if (open !== null) {
    return (
      <OnboardingWorkspace
        onboardingId={open}
        employees={employees}
        onBack={() => {
          setOpen(null);
          reload();
        }}
        onChanged={reload}
      />
    );
  }

  const rows = list.data ?? [];

  return (
    <>
      {/* The task library used to be a second view here. It is a page of its
          own now — Client Onboarding, under Operate — because the account
          managers who inherit the client are the ones who maintain it. What
          Sales needs from this tab is the handover, so what is left is the
          list and the way in.

          Writing a task ON A CLIENT is untouched and lives where it always
          did, in the workspace: that task is copied onto the onboarding with
          no TemplateId and never enters the library. The two are separate on
          purpose — an ad-hoc task for one client is not a decision about what
          every client gets. */}
      {/* Right-aligned rather than row-between with an empty span holding the
          left half open: there is nothing on the left any more. */}
      <div className="row wrap mb-4" style={{ justifyContent: 'flex-end' }}>
        <button type="button" className="btn btn-primary" onClick={() => setWizard(true)}>
          Create a handover
        </button>
      </div>

      {list.loading && <Loading label="Loading onboardings" />}
      {list.error && <ErrorBanner message={list.error} />}

      {/* Just the button until there is a handover: no figures, no empty-state box. */}
      {rows.length > 0 && (
        <div className="onb-list">
          {rows.map((row) => {
            const pct = progressPct(row.TasksComplete, row.TaskCount);
            const handedOver = row.Status === 'handed_over';
            const ready = !handedOver && row.TaskCount > 0 && row.TasksComplete === row.TaskCount;
            return (
              <button
                key={row.OnboardingId}
                type="button"
                className="onb-row"
                onClick={() => setOpen(row.OnboardingId)}
              >
                <div className="grow">
                  <div className="row wrap">
                    <span className="strong">{row.AccountName ?? 'Unnamed client'}</span>
                    {handedOver ? (
                      <StatusChip band="good">With Account Management</StatusChip>
                    ) : ready ? (
                      <StatusChip band="warning">Ready to hand over</StatusChip>
                    ) : row.TasksOverdue > 0 ? (
                      <StatusChip band="critical">
                        {row.TasksOverdue} overdue
                      </StatusChip>
                    ) : row.Status === 'cancelled' ? (
                      <StatusChip band="neutral">Cancelled</StatusChip>
                    ) : (
                      <StatusChip band="neutral">In progress</StatusChip>
                    )}
                  </div>
                  <p className="xs subtle mt-2">
                    {[
                      [row.ContactFirstName, row.ContactLastName].filter(Boolean).join(' ') ||
                        null,
                      row.ContactEmail,
                      row.Industry,
                      row.UserCount ? `${row.UserCount} users` : null,
                      row.Mrr ? `${formatNumber(row.Mrr, 'currency')}/mo` : null,
                      handedOver
                        ? `handed over ${formatDate(row.HandedOverUtc)}`
                        : `started ${formatDate(row.SubmittedUtc)}`,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                </div>

                <div className="onb-row-progress">
                  <span className="xs subtle">
                    {row.TaskCount === 0
                      ? 'no tasks'
                      : `${row.TasksComplete}/${row.TaskCount} · ${pct}%`}
                  </span>
                  <span className="onb-bar" aria-hidden="true">
                    <span style={{ width: `${pct}%` }} />
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {wizard && (
        <OnboardingWizard
          accounts={accounts}
          deals={deals}
          defaultAccountId={defaultAccountId}
          onClose={() => setWizard(false)}
        />
      )}
    </>
  );
}
